@let newsItemsLoading = newsItemsLoading$ | async;
@let editingItem = editingItem$ | async;

<div class="manage-newsfeed-container">
  <mat-tab-group class="show-on-mobile" [(selectedIndex)]="selectedTab">
    <mat-tab label="{{ 'split_pane.navigation.news_items' | translate }}">
      <ng-template matTabContent>
        <div class="newsfeed-list-container">
          <div class="newsfeed-list-content">
            <ng-container *ngTemplateOutlet="newsfeedList"></ng-container>
          </div>
        </div>
      </ng-template>
    </mat-tab>
    <mat-tab label="{{ editingItem || newsItemsLoading ? ('organization_unit.manage_newsfeed.edit.title' | translate) : ('organization_unit.manage_newsfeed.publish.title' | translate) }}">
      <ng-template matTabContent>
        <div class="newsfeed-details-container">
          <div class="newsfeed-details-content">
            <ng-container *ngTemplateOutlet="newsfeedDetails"></ng-container>
          </div>
        </div>
      </ng-template>
    </mat-tab>
  </mat-tab-group>

  <div class="show-on-desktop">
    <div class="newsfeed-list-container">
      <div class="newsfeed-list-content">
        <ng-container *ngTemplateOutlet="newsfeedList"></ng-container>
      </div>
    </div>
    <div class="newsfeed-details-container">
      <div class="newsfeed-details-content">
        <ng-container *ngTemplateOutlet="newsfeedDetails"></ng-container>
      </div>
    </div>
  </div>
</div>

<ng-template #newsfeedList>
  <div class="flex-wrap flex-row justify-between items-center">
    <h2>{{ 'organization_unit.manage_newsfeed.title' | translate }}</h2>
    <button mat-icon-button (click)="clear()" [matTooltip]="'organization_unit.manage_newsfeed.add.tooltip' | translate">
      <mat-icon> add </mat-icon>
    </button>
  </div>

  <mat-form-field appearance="outline" class="news-status">
    <mat-select [(value)]="archivedSelection" (selectionChange)="fetchNewsItems(); currentPage = 1">
      @for (item of statusFilterOptions; track item) {
        <mat-option [value]="item.value">{{ item.label | translate }}</mat-option>
      }
    </mat-select>
  </mat-form-field>

  <div class="news-item-list-wrapper">
    @if (!newsItemsLoading) {
      @if (newsItems$ | async; as newsItems) {
        <card-list [class.archive]="archivedSelection === 'archived'">
          @for (item of newsItems | paginate: (paginationConfig$ | async); track item) {
            <card-list-item
              #trigger
              [selected]="editingItem?.id === item.id"
              (click)="editNewsItem(item)"
              [mediaUuid]="item.media_uuid"
              class="news-item-card"
              >
              <span>{{ item.subject }} </span>
              <span>{{ item.show_from | i18n_date | async }} • {{ item.organization_unit_name }} </span>
              <span [innerHTML]="item.content | htmlToPlaintext"></span>
              <div class="list-item-stats justify-center items-center">
                <span
                  [matTooltip]="'organization_unit.manage_newsfeed.read_count.tooltip' | translate: { readCount: item.news_read }"
                  class="justify-center items-center"
                  >
                  <mat-icon>visibility</mat-icon>&nbsp; {{ item.news_read }}</span
                  >
                </div>
              </card-list-item>
            }
          </card-list>
        }
      } @else {
        <div class="spinner justify-center items-center" style="width: 100%; height: 100%;">
          <mat-spinner diameter="48"></mat-spinner>
        </div>
      }
    </div>
    @if ((paginationConfig$ | async).totalItems > MAX_ITEMS_PER_PAGE) {
      <div class="flex-row justify-center items-center">
        <pagination-controls
          id="organizationUnitManageNewsfeed"
          [nextLabel]="'generic.pagination.next' | translate"
          [previousLabel]="'generic.pagination.previous' | translate"
          (pageChange)="paginate($event)"
        ></pagination-controls>
      </div>
    }
</ng-template>

<ng-template #newsfeedDetails>
  <div class="form-wrapper flex-col">
    <div class="message-form" [class]="(bounceInTrigger$ | async) ? 'bounce' : ''">
      <div class="justify-between items-center">
        <h2>
          {{ editingItem ? ('organization_unit.manage_newsfeed.edit.title' | translate) : ('organization_unit.manage_newsfeed.publish.title' | translate) }}
        </h2>
        @if (editingItem) {
          <div>
            <button
              mat-icon-button
              (click)="deleteOrRecover()"
          [matTooltip]="
            archivedSelection === 'archived'
              ? ('organization_unit.manage_newsfeed.recover.tooltip' | translate)
              : ('organization_unit.manage_newsfeed.delete.tooltip' | translate)
          "
              [color]="archivedSelection === 'archived' ? 'accent' : 'warn'"
              >
              <mat-icon> {{ archivedSelection === 'archived' ? 'restore_from_trash' : 'delete' }} </mat-icon>
            </button>
            <button mat-icon-button (click)="viewItem(editingItem.id)" [matTooltip]="'organization_unit.manage_newsfeed.preview.tooltip' | translate">
              <mat-icon>preview</mat-icon>
            </button>
          </div>
        }
      </div>
      @if (newsItemForm) {
        <form [formGroup]="newsItemForm" class="flex-col gap-4">
          <mat-form-field class="title-input">
            <mat-label>{{ 'newsfeed.item.title.label' | translate }}</mat-label>
            <input
              autocomplete="off"
              autocapitalize="sentences"
              (input)="this.newsItemForm.markAsTouched()"
              [required]="true"
              formControlName="subject"
              matInput
              [errorStateMatcher]="errorStateMatcher"
              />
          </mat-form-field>
          <app-asset-upload identifier="upload" (uploaded)="onImageSelected($event)">
            <ng-template let-uploadData="uploadData" let-fileInput="fileInput">
              <div class="image-container">
                <app-doenkids-image [mediaItem]="newsItemForm.get('media_uuid').value" [preview]="false" [isRequired]="true"></app-doenkids-image>
              </div>
              <div class="media-select-buttons justify-center items-center">
                <app-select-media [uploadData]="uploadData" (selected)="onImageSelected($event)" identifier="upload"></app-select-media>
              </div>
              <span class="tip">{{ 'newsfeed.item.image.description' | translate }}</span>
            </ng-template>
          </app-asset-upload>
          <hr />
          <div class="content-input">
            <quill-editor
              theme="snow"
              formControlName="content"
              bounds="self"
              [required]="true"
              [class.invalid]="contentInvalid$ | async"
              appOnFocus
              [placeholder]="'newsfeed.item.content.placeholder' | translate"
              (onBlur)="onContentBlur($event)"
              (onContentChanged)="onContentChange($event)"
              >
            </quill-editor>
          </div>
          <hr />
          <div class="radio-buttons">
            <h4>{{ 'newsfeed.item.visibility.title' | translate }}</h4>
            <mat-radio-group
              [attr.aria-label]="'newsfeed.item.visibility.label' | translate"
              class="flex-wrap justify-between flex-col"
              formControlName="news_type"
              (change)="onNewsTypeChange($event)"
              >
              <mat-radio-button value="ORGANIZATION"> {{ 'newsfeed.item.visibility.organization' | translate }}</mat-radio-button>
              <mat-radio-button value="ORGANIZATION_TREE">
                {{ 'newsfeed.item.visibility.organization_tree' | translate }}
              </mat-radio-button>
            </mat-radio-group>
          </div>
          <hr />
          @if (activityTypes$ | async; as activityTypes) {
            @if (newsItemForm.get('news_type').value === 'ORGANIZATION_TREE' && activityTypes.length > 1) {
              <div>
                <h4>{{ 'newsfeed.item.visibility.organization_tree.locations.title' | translate }}</h4>
                <mat-selection-list
                  formControlName="activity_type_id"
                  (selectionChange)="newsItemForm.markAsTouched()"
                  [class.oko-invalid]="newsItemForm.get('activity_type_id').invalid"
                  >
                  @for (option of activityTypes; track option) {
                    <mat-list-option [value]="option.id"> {{ option.name }} </mat-list-option>
                  }
                </mat-selection-list>
                <hr />
              </div>
            }
          }
          <div class="date-area flex-col">
            <h4>{{ 'newsfeed.item.visibility.period.title' | translate }}</h4>
            <div class="flex-row flex-1 gap-4 flex-wrap">
              <mat-form-field appearance="fill">
                <mat-label>{{ 'newsfeed.item.visibility.from.label' | translate }}</mat-label>
                <input
                  autocomplete="off"
                  [min]="showFromMin"
                  formControlName="show_from"
                  matInput
                  [matDatepicker]="fromPicker"
                  [required]="true"
                  (dateChange)="dateChange()"
                  [errorStateMatcher]="errorStateMatcher"
                  />
                <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
                <mat-datepicker #fromPicker></mat-datepicker>
              </mat-form-field>
              <mat-form-field appearance="fill">
                <mat-label>{{ 'newsfeed.item.visibility.to.label' | translate }}</mat-label>
                <input
                  autocomplete="off"
                  [min]="this.newsItemForm.value.show_from"
                  formControlName="show_until"
                  matInput
                  [matDatepicker]="toPicker"
                  (dateChange)="dateChange()"
                  [errorStateMatcher]="errorStateMatcher"
                  />
                <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
                <mat-datepicker #toPicker></mat-datepicker>
                @if (this.newsItemForm.value.show_until) {
                  <button
                    matSuffix
                    mat-icon-button
                    [attr.aria-label]="'newsfeed.item.visibility.to.clear.label' | translate"
                    (click)="newsItemForm.get('show_until').setValue(null)"
                    >
                    <mat-icon>close</mat-icon>
                  </button>
                }
              </mat-form-field>
            </div>
          </div>
          @if (this.newsItemForm.value.show_until) {
            <span
        [innerHtml]="
          'newsfeed.item.visibility.period.from_to.description'
            | translate
              : {
                  showFrom: this.newsItemForm.value.show_from | i18n_date: 'EEE. d LLLL' | async,
                  showTo: this.newsItemForm.value.show_until | i18n_date: 'EEE. d LLLL' | async
                }
        "
          ></span>
        }
        @if (!this.newsItemForm.value.show_until) {
          <span
        [innerHtml]="
          'newsfeed.item.visibility.period.indefinite_from.description'
            | translate
              : {
                  showFrom: this.newsItemForm.value.show_from | i18n_date: 'EEE. d LLLL' | async
                }
        "
        ></span>
      }
      <div class="buttons flex-row items-center gap-4">
        <button mat-stroked-button (click)="clear()">{{ 'generic.cancel' | translate }}</button>
        <button color="primary" class="publish-button" mat-flat-button (click)="publish()" [disabled]="!newsItemForm.valid || !newsItemForm.touched">
          <mat-icon>publish</mat-icon>
          {{
          editingItem ? ('organization_unit.manage_newsfeed.publish_changes' | translate) : ('organization_unit.manage_newsfeed.publish_item' | translate)
          }}
        </button>
      </div>
    </form>
  }
  </div>
  </div>
</ng-template>

<ng-template #spinner>
  <div class="spinner justify-center items-center" style="width: 100%; height: 100%;">
    <mat-spinner diameter="48"></mat-spinner>
  </div>
</ng-template>